import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { JobTitle, NewJobTitle } from '../../../interfaces/job-title.interface';
import { InsuranceCompany } from '../../../interfaces/insurance-company.interface';
import { EditIconButton } from '../../../shared/components';
import { ExcelMappingDialogComponent, ExcelMappingData, MappingResult } from './excel-mapping-dialog';
import { UploadService } from '../../../services/upload.service';
import { SystemSettingsService } from '../../../services/system-settings.service';
import { AutoLogoutService } from '../../../services/auto-logout.service';
import { DebugDateControlComponent } from './debug-date-control/debug-date-control.component';
import { ContractListPreviewComponent, ContractListDialogData, ExcelData } from './contract-list-preview';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'admin-settings',
  standalone: true,
  imports: [FormsModule, MatIconModule, MatTooltipModule, EditIconButton, DebugDateControlComponent],
  template: `
    <div class="tab-content">
      <div class="settings-grid">
        
        <!-- Táblák bővítése -->
        <div class="card">
          <div class="card-header">
            <h3>Munkakörök kezelése</h3>
            <button class="btn btn-primary btn-sm" (click)="onOpenJobTitleForm()" title="Új munkakör hozzáadása">
              <mat-icon>add</mat-icon>
            </button>
          </div>
          <div class="settings-content">
            <!-- Munkakörök listája -->
            <div class="setting-item">
              
              <div class="job-titles-list">
                @for (jobTitle of jobTitles; track jobTitle.id) {
                  <div class="job-title-item">
                    <div class="job-title-info">
                      <span class="job-title-name">{{ jobTitle.title_name_hu }}</span>
                      <span class="job-title-en">{{ jobTitle.title_name_en }}</span>
                    </div>
                    <div class="job-title-right">
                      <span class="job-title-status" [class]="jobTitle.is_active ? 'active' : 'inactive'">
                        {{ jobTitle.is_active ? 'Aktív' : 'Inaktív' }}
                      </span>
                      <div class="job-title-actions">
                        <app-edit-icon-button (clicked)="onEditJobTitle(jobTitle)" title="Munkakör szerkesztése"></app-edit-icon-button>
                      </div>
                    </div>
                  </div>
                } @empty {
                  <div class="no-job-titles">
                    Még nincsenek munkakörök meghatározva.
                  </div>
                }
              </div>
            </div>
          </div>
        </div>

        <!-- Biztosítók kezelése -->
        <div class="card">
          <div class="card-header">
            <h3>Biztosítók kezelése</h3>
            <button class="btn btn-primary btn-sm" (click)="onOpenInsuranceCompanyForm()" title="Új biztosító hozzáadása">
              <mat-icon>add</mat-icon>
            </button>
          </div>
          <div class="settings-content">
            <!-- Biztosítók listája -->
            <div class="setting-item">
              
              <div class="job-titles-list">
                @for (insuranceCompany of insuranceCompanies; track insuranceCompany.id) {
                  <div class="job-title-item">
                    <div class="job-title-info">
                      <span class="job-title-name">{{ insuranceCompany.companyname }}</span>
                    </div>
                    <div class="job-title-right">
                      <div class="job-title-actions">
                        <app-edit-icon-button (clicked)="onEditInsuranceCompany(insuranceCompany)" title="Biztosító szerkesztése"></app-edit-icon-button>
                      </div>
                    </div>
                  </div>
                } @empty {
                  <div class="no-job-titles">
                    Még nincsenek biztosítók meghatározva.
                  </div>
                }
              </div>
            </div>
          </div>
        </div>

        <!-- Export Settings -->
        <div class="card">
          <div class="card-header">
            <h3><i class="icon-download"></i> Excel import</h3>
          </div>
          <div class="settings-content">
            <div class="export-buttons">
              <button class="btn btn-outline" (click)="triggerFileUpload()">
                <i class="icon-file-excel"></i>
                Adatbázis feltöltés
              </button>
              <input 
                #fileInput 
                type="file" 
                accept=".xlsx,.xls" 
                style="display: none;"
                (change)="onFileSelected($event)">
              <button class="btn btn-outline" (click)="triggerContractListUpload()">
                <i class="icon-file-pdf"></i>
                Állománylista frissítése
              </button>
              <input 
                #contractFileInput 
                id="contractFileInput"
                type="file" 
                accept=".xlsx,.xls" 
                style="display: none;"
                (change)="onContractFileSelected($event)">
            </div>
          </div>
        </div>

        <!-- Backup Settings -->
        <div class="card">
          <div class="card-header">
            <h3><i class="icon-cloud"></i> Backup & Visszaállítás</h3>
          </div>
          <div class="settings-content">
            <div class="backup-status">
              <div class="status-item">
                <span class="status-label">Utolsó manuális backup:</span>
                <span class="status-value">{{ lastManualBackupTime || 'Még nem készült' }}</span>
              </div>
              <div class="status-item">
                <span class="status-label">Következő backup:</span>
                <span class="status-value">2025. június 26. 14:30</span>
              </div>
            </div>
            
            <div class="backup-actions">
              <button class="btn btn-primary" (click)="onCreateBackup()">
                <i class="icon-cloud-upload"></i>
                Azonnali Backup
              </button>
              <button class="btn btn-outline" (click)="onRestoreBackup()">
                <i class="icon-cloud-download"></i>
                Visszaállítás
              </button>
            </div>
            
            <div class="setting-item">
              <label class="toggle-label">
                <input type="checkbox" checked>
                <span class="toggle-slider"></span>
                Automatikus napi backup
              </label>
            </div>
          </div>
        </div>
        
        <!-- Biztonság beállítások -->
        <div class="card">
          <div class="card-header">
            <h3><mat-icon>security</mat-icon> Biztonság</h3>
          </div>
          <div class="settings-content">
            <div class="setting-item">
              <label class="toggle-label disabled">
                <input type="checkbox" disabled>
                <span class="toggle-slider"></span>
                Kétfaktoros hitelesítés
              </label>
            </div>
            
            <div 
              class="setting-item"
              [matTooltip]="autoLogoutEnabled ? 'Bekapcsolva: 60 perc inaktivitás után automatikus kijelentkezés' : 'Kikapcsolva: Nincs automatikus kijelentkezés'"
              matTooltipPosition="right"
              matTooltipShowDelay="300">
              <label class="toggle-label">
                <input 
                  type="checkbox" 
                  [(ngModel)]="autoLogoutEnabled"
                  (change)="onAutoLogoutToggle()">
                <span class="toggle-slider"></span>
                Automatikus kijelentkezés (60 perc)
              </label>
            </div>
            
            <div class="setting-item">
              <label>Jelszó minimum hossza</label>
              <select>
                <option value="6">6 karakter</option>
                <option value="8" selected>8 karakter</option>
                <option value="12">12 karakter</option>
              </select>
            </div>
          </div>
        </div>
        
        <!-- Értesítések -->
        <div class="card">
          <div class="card-header">
            <h3><mat-icon>notifications</mat-icon> Értesítések</h3>
          </div>
          <div class="settings-content">
            <div class="setting-item">
              <label class="toggle-label">
                <input type="checkbox" checked>
                <span class="toggle-slider"></span>
                E-mail értesítések
              </label>
            </div>
            
            <div class="setting-item">
              <label class="toggle-label">
                <input type="checkbox" checked>
                <span class="toggle-slider"></span>
                Böngészős értesítések
              </label>
            </div>
            
            <div class="setting-item">
              <label class="toggle-label">
                <input type="checkbox">
                <span class="toggle-slider"></span>
                SMS értesítések
              </label>
            </div>
          </div>
        </div>

        <!-- Debug Dátum Beállítás -->
        <div class="card">
          <div class="card-header">
            <h3><mat-icon>bug_report</mat-icon> Debug Dátum Beállítás</h3>
          </div>
          <div class="settings-content">
            <app-debug-date-control></app-debug-date-control>
          </div>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./admin-settings.component.scss']
})
export class AdminSettingsComponent implements OnInit {
  @Input() jobTitles: JobTitle[] = [];
  @Input() insuranceCompanies: InsuranceCompany[] = [];
  @Input() lastManualBackupTime: string | null = null; // Most már valóban csak manuális backup-okat mutat
  
  @Output() openJobTitleForm = new EventEmitter<void>();
  @Output() editJobTitle = new EventEmitter<JobTitle>();
  @Output() openInsuranceCompanyForm = new EventEmitter<void>();
  @Output() editInsuranceCompany = new EventEmitter<InsuranceCompany>();
  @Output() createBackup = new EventEmitter<void>();
  @Output() restoreBackup = new EventEmitter<void>();

  // Automatikus kijelentkezés állapot
  autoLogoutEnabled: boolean = true;

  constructor(
    private uploadService: UploadService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private systemSettingsService: SystemSettingsService,
    private autoLogoutService: AutoLogoutService
  ) {}

  ngOnInit(): void {
    // Beállítás betöltése
    this.loadAutoLogoutSetting();
  }

  /**
   * Automatikus kijelentkezés beállítás betöltése
   */
  loadAutoLogoutSetting(): void {
    this.systemSettingsService.getAutoLogoutSetting().subscribe({
      next: (response) => {
        this.autoLogoutEnabled = response.enabled;
        this.autoLogoutService.updateSettings(response.enabled);
      },
      error: (error) => {
        console.error('Hiba a beállítás betöltésekor:', error);
        this.snackBar.open('Hiba a beállítás betöltésekor', 'Bezár', { duration: 3000 });
      }
    });
  }

  /**
   * Automatikus kijelentkezés kapcsoló kezelése
   */
  onAutoLogoutToggle(): void {
    this.systemSettingsService.updateAutoLogoutSetting(this.autoLogoutEnabled).subscribe({
      next: (response) => {
        this.autoLogoutService.updateSettings(response.enabled);
        this.snackBar.open(
          response.message,
          'Bezár',
          { 
            duration: 3000,
            panelClass: 'success-snackbar'
          }
        );
        
        // Frissítsük a jelenlegi állapotot
        this.autoLogoutEnabled = response.enabled;
      },
      error: (error) => {
        console.error('Hiba a beállítás mentésekor:', error);
        // Visszaállítjuk az előző értéket hiba esetén
        this.autoLogoutEnabled = !this.autoLogoutEnabled;
        this.snackBar.open(
          'Hiba történt a beállítás mentése során',
          'Bezár',
          { 
            duration: 5000,
            panelClass: 'error-snackbar'
          }
        );
      }
    });
  }

  onOpenJobTitleForm() {
    this.openJobTitleForm.emit();
  }

  onEditJobTitle(jobTitle: JobTitle) {
    this.editJobTitle.emit(jobTitle);
  }

  onOpenInsuranceCompanyForm() {
    this.openInsuranceCompanyForm.emit();
  }

  onEditInsuranceCompany(insuranceCompany: InsuranceCompany) {
    this.editInsuranceCompany.emit(insuranceCompany);
  }

  onCreateBackup() {
    this.createBackup.emit();
  }

  onRestoreBackup() {
    this.restoreBackup.emit();
  }

  triggerFileUpload() {
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      this.snackBar.open(`Fájl kiválasztva: ${file.name}`, 'OK', {
        duration: 0 // Nem tűnik el automatikusan
      });
      this.uploadExcelFile(file);
    }
  }

  uploadExcelFile(file: File) {
    this.snackBar.open('Excel fájl feltöltése megkezdődött...', 'OK', {
      duration: 0
    });
    this.uploadService.uploadExcelFile(file).subscribe({
      next: (response) => {
        // Duplikált cégek kezelése
        let message = `Excel feltöltés sikeres! Fájl: ${response.filename}, Sorok: ${response.rows_count}`;
        
        if (response.skipped_duplicates && response.skipped_duplicates > 0) {
          message += `\n⚠️ ${response.skipped_duplicates} cég már létezik az adatbázisban`;
          
          // Részletes információ a duplikált cégekről
          // if (response.duplicate_details && response.duplicate_details.length > 0) {
          //   console.warn('🔍 Már létező cégek részletei:');
          //   response.duplicate_details.forEach(dup => {
          //     console.warn(`  - Sor ${dup.row_index}: ${dup.company_name}`);
          //     console.warn(`    Egyezés típusa: ${dup.match_type}`);
          //     console.warn(`    Létező cég ID: ${dup.existing_company_id} (${dup.existing_company_name})`);
          //     console.warn(`    Indok: ${dup.reason}`);
          //   });
          // }
        }
        
        this.snackBar.open(message, 'OK', {
          duration: response.skipped_duplicates && response.skipped_duplicates > 0 ? 8000 : 5000
        });
        
        // Mapping dialog megnyitása
        this.openMappingDialog(response);
      },
      error: (error) => {
        this.snackBar.open(`Hiba történt az Excel fájl feltöltése során: ${error.error?.detail || 'Ismeretlen hiba'}`, 'OK', {
          duration: 0
        });
      }
    });
  }

  private openMappingDialog(uploadResponse: any): void {
    const dialogData: ExcelMappingData = {
      filename: uploadResponse.filename,
      columns: uploadResponse.columns,
      data: uploadResponse.data
    };

    const dialogRef = this.dialog.open(ExcelMappingDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: dialogData,
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result && result.success) {
        // Csak az új cégekhez tartozó rekordokat mutatjuk (kivéve company_customers)
        const newRecords = result.result.new_company_records || {};
        const recordsInfo = Object.entries(newRecords)
          .filter(([table]) => table !== 'company_customers')
          .map(([table, count]) => `${table}: ${count} db`)
          .join(', ');
        
        const duplicatesInfo = result.result.skipped_duplicates > 0 
          ? ` (${result.result.skipped_duplicates} létező cég kihagyva)` 
          : '';
        
        const enrichedInfo = result.result.enriched_companies > 0
          ? ` (${result.result.enriched_companies} meglévő cég kiegészítve)`
          : '';
        
        const invalidIdentifiersInfo = result.result.skipped_invalid_identifiers > 0
          ? ` (${result.result.skipped_invalid_identifiers} cég érvénytelen azonosító miatt kihagyva)`
          : '';
        
        const message = recordsInfo 
          ? `Sikeres feltöltés! Új rekordok: ${recordsInfo}${enrichedInfo}${duplicatesInfo}${invalidIdentifiersInfo}`
          : result.result.enriched_companies > 0
            ? `Sikeres feltöltés!${enrichedInfo}${duplicatesInfo}${invalidIdentifiersInfo}`
            : result.result.skipped_invalid_identifiers > 0
              ? `Sikeres feltöltés, de ${result.result.skipped_invalid_identifiers} cég érvénytelen azonosító miatt kihagyva${duplicatesInfo}`
              : `Sikeres feltöltés! Minden cég már létezett az adatbázisban (${result.result.skipped_duplicates} db)`;
        
        this.snackBar.open(message, 'OK', { duration: 10000 });
        
        // Automatikus riport letöltés
        console.log('🔍 Report path ellenőrzés:', {
          result: result,
          resultResult: result.result,
          reportPath: result.result?.report_path,
          reportError: result.result?.report_error
        });
        
        if (result.result.report_error) {
          console.error('❌ Riport generálás hiba (backend):', result.result.report_error);
        }
        
        if (result.result.report_path) {
          const filename = result.result.report_path.split('/').pop();
          console.log('📄 Riport letöltés indítása:', filename);
          this.downloadReport(filename);
        } else {
          console.warn('⚠️ Nincs report_path a válaszban!');
          if (result.result.report_error) {
            this.snackBar.open(`Riport generálás sikertelen: ${result.result.report_error.substring(0, 100)}`, 'OK', { duration: 10000 });
          }
        }
      } else if (result && !result.success) {
        this.snackBar.open(
          `Hiba történt a feltöltés során: ${result.error?.message || 'Ismeretlen hiba'}`, 
          'OK', 
          {
            duration: 0
          }
        );
      } else {
        this.snackBar.open('Feltöltés megszakítva', 'OK', {
          duration: 0
        });
      }
    });
  }

  // Contract List Preview metódusok
  triggerContractListUpload() {
    const contractFileInput = document.querySelector('#contractFileInput') as HTMLInputElement;
    if (!contractFileInput) {
      // Ha nem találjuk az input-ot ID alapján, keressük típus szerint
      const fileInputs = document.querySelectorAll('input[type="file"]') as NodeListOf<HTMLInputElement>;
      for (let input of fileInputs) {
        if (input.accept === '.xlsx,.xls' && input !== document.querySelector('#fileInput')) {
          input.click();
          return;
        }
      }
    } else {
      contractFileInput.click();
    }
  }

  onContractFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      this.snackBar.open(`Állománylista fájl kiválasztva: ${file.name}`, 'OK', {
        duration: 3000
      });
      this.previewContractListFile(file);
    }
  }

  previewContractListFile(file: File) {
    this.snackBar.open('Állománylista betöltése...', 'OK', {
      duration: 0
    });
    
    // A teljes Excel fájlt töltjük fel, hogy minden sort megkapjunk
    this.uploadService.uploadExcelFile(file).subscribe({
      next: (response) => {
        const excelData: ExcelData = {
          filename: response.filename,
          rows_count: response.rows_count,
          columns: response.columns,
          data: response.data
        };
        
        this.snackBar.dismiss(); // Bezárjuk a loading üzenetet
        this.openContractListDialog(excelData);
      },
      error: (error) => {
        this.snackBar.open(
          `Hiba az állománylista betöltése során: ${error.error?.detail || 'Ismeretlen hiba'}`, 
          'OK', 
          {
            duration: 0
          }
        );
      }
    });
  }

  openContractListDialog(excelData: ExcelData) {
    const dialogData: ContractListDialogData = {
      excelData: excelData
    };

    const dialogRef = this.dialog.open(ContractListPreviewComponent, {
      width: '95vw',
      maxWidth: '1400px',
      height: '90vh',
      maxHeight: '900px',
      data: dialogData,
      disableClose: false,
      panelClass: ['contract-list-dialog-panel', 'crm-dialog']
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.action === 'process') {
        this.snackBar.open(
          'Állománylista feldolgozása megkezdődött...', 
          'OK', 
          {
            duration: 3000
          }
        );
        // Itt lehet majd implementálni a további feldolgozást
      }
    });
  }

  /**
   * Riport letöltése blob-on keresztül
   */
  private downloadReport(filename: string): void {
    const downloadUrl = `${environment.apiUrl}/upload/report/download/${filename}`;
    console.log('🔗 Riport letöltési URL:', downloadUrl);
    
    // Fetch-el lekérjük a fájlt blob-ként
    fetch(downloadUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    })
    .then(response => {
      console.log('📥 Fetch response:', response.status, response.statusText);
      if (!response.ok) {
        throw new Error(`Riport letöltés sikertelen: ${response.status} ${response.statusText}`);
      }
      return response.blob();
    })
    .then(blob => {
      console.log('📦 Blob mérete:', blob.size, 'bytes');
      // Blob URL létrehozása és letöltés triggerelése
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      console.log('✅ Riport letöltve:', filename);
    })
    .catch(error => {
      console.error('❌ Riport letöltés hiba:', error);
      this.snackBar.open('Riport letöltés sikertelen', 'OK', { duration: 3000 });
    });
  }


}
